import { useEffect, useMemo, useRef, useState } from 'react';
import { sanitizeSvg } from '../utils/svgSanitize.js';
import { useLearningEngine } from '../hooks/useLearningEngine.js';
import { TRACKS, PHASE_META } from '../constants/appConstants.js';
import { fetchRoadmapProgress } from '../services/supabaseClient.js';
import QuizPanel from '../components/QuizPanel.jsx';
import PracticeCard from '../components/shared/PracticeCard.jsx';
import LessonCompleteOverlay from '../components/shared/LessonCompleteOverlay.jsx';
import LessonProgressBar from '../components/study/LessonProgressBar.jsx';
import { getLearnerState, getNotes } from '../services/supabaseClient.js';
import { isEntertainmentHit, rerankCandidates } from '../utils/youtubeFilters.js';

// ─────────────────────────────────────────────
// COLOR TOKENS
// ─────────────────────────────────────────────
const C = {
  bg:        'var(--bg-primary)',
  panel:     'var(--bg-secondary)',
  cream:     'var(--bg-card)',             // assistant chat bubble surface
  creamText: 'var(--text-primary)',
  teal:      'var(--brand-emerald)',       // emerald action color — Continue Lesson, Check Understanding, Take a Break
  tealText:  '#ffffff',                    // white reads cleanly on emerald
  muted:     'var(--text-muted)',
  subtle:    'var(--bg-elevated)',
  border:    'var(--border-soft)',
};

// ─────────────────────────────────────────────
// PROGRESS BAR
// ─────────────────────────────────────────────
function ProgressBar({ value, max, color, label }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        {label && <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</span>}
        <span style={{ fontSize: 10, color: C.muted }}>{pct}%</span>
      </div>
      <div style={{ height: 5, background: 'var(--border-medium)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color || '#2dd4bf', borderRadius: 99, transition: 'width 0.6s ease' }}/>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// CHECKPOINT RETRY — fail-closed UI when a checkpoint produced no quiz
// ─────────────────────────────────────────────
function CheckpointRetry({ state, onRetry, onContinue }) {
  return (
    <div style={{ background: 'var(--bg-elevated)', border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
      {!state.showContinueButton && (
        <div style={{ display: 'flex', gap: 5 }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent-primary)', animation: `dot 1.2s ease ${i*0.2}s infinite`, opacity: 0.4 }}/>)}
        </div>
      )}
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, fontWeight: 500, lineHeight: 1.5 }}>{state.message}</p>
      {state.showRetryButton && (
        <button className="lesson-btn" style={{ marginTop: 2 }} onClick={onRetry}>Try Again</button>
      )}
      {state.showContinueButton && (
        <button className="lesson-btn" style={{ marginTop: 2 }} onClick={onContinue}>Continue Lesson</button>
      )}
      <style>{`@keyframes dot{0%,80%,100%{opacity:.2;transform:scale(.7)}40%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// INLINE FORMATTER
// ─────────────────────────────────────────────
function inlineFormat(text) {
  if (!text) return '';
  const parts = [];
  let remaining = text;
  let key = 0;
  // Order matters: bold (**) is searched before italic (*) so **foo** is never
  // mis-parsed as * + *foo* + *. Italic content excludes * and a leading space,
  // so "2 * 3 * 4" and stray bullet markers don't trigger emphasis.
  const RX = {
    bold:  /\*\*(.+?)\*\*/,
    code:  /`(.+?)`/,
    ital:  /\*([^*\s][^*\n]*?)\*/,
    under: /_([^_\s][^_\n]*?)_/,
  };
  while (remaining.length > 0) {
    const idx = {
      bold:  remaining.search(RX.bold),
      code:  remaining.search(RX.code),
      ital:  remaining.search(RX.ital),
      under: remaining.search(RX.under),
    };
    const found = Object.values(idx).filter(x => x !== -1);
    if (found.length === 0) { parts.push(remaining); break; }
    const first = Math.min(...found);
    if (first > 0) parts.push(remaining.slice(0, first));
    const m = {
      bold:  remaining.match(RX.bold),
      code:  remaining.match(RX.code),
      ital:  remaining.match(RX.ital),
      under: remaining.match(RX.under),
    };
    // Tie-break priority at the same index: bold > code > italic > underscore.
    if (first === idx.bold && m.bold) {
      parts.push(<strong key={key++} style={{ color: C.creamText, fontWeight: 700 }}>{m.bold[1]}</strong>);
      remaining = remaining.slice(idx.bold + m.bold[0].length);
    } else if (first === idx.code && m.code) {
      parts.push(<code key={key++} style={{ background: 'var(--accent-soft)', color: 'var(--accent-primary)', padding: '1px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>{m.code[1]}</code>);
      remaining = remaining.slice(idx.code + m.code[0].length);
    } else if (first === idx.ital && m.ital) {
      parts.push(<em key={key++}>{m.ital[1]}</em>);
      remaining = remaining.slice(idx.ital + m.ital[0].length);
    } else if (first === idx.under && m.under) {
      parts.push(<em key={key++}>{m.under[1]}</em>);
      remaining = remaining.slice(idx.under + m.under[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// ─────────────────────────────────────────────
// TABLE
// ─────────────────────────────────────────────
function MdTable({ lines, color }) {
  const parse = l => l.split('|').map(c => c.trim()).filter(Boolean);
  const headers = parse(lines[0]);
  const rows    = lines.slice(2).map(parse).filter(r => r.length > 0);
  return (
    <div style={{ overflowX: 'auto', margin: '10px 0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr>{headers.map((h, i) => (
          <th key={i} style={{ padding: '7px 11px', textAlign: 'left', fontWeight: 700, background: `${color}18`, color, borderBottom: `1px solid ${color}33` }}>{inlineFormat(h)}</th>
        ))}</tr></thead>
        <tbody>{rows.map((row, ri) => (
          <tr key={ri} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            {row.map((cell, ci) => <td key={ci} style={{ padding: '6px 11px', color: C.creamText, background: ri % 2 ? 'var(--bg-elevated)' : 'transparent' }}>{inlineFormat(cell)}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────
// MARKDOWN RENDERER
// ─────────────────────────────────────────────
// Typed callouts — Note / Tip / Exam Tip / Watch Out / Worked Example /
// Scenario. Detected from leading-label prose so the lesson reads as designed
// blocks rather than a wall of paragraphs.
const CALLOUT_RE = /^\*{0,2}\s*(exam tip|watch out|caution|warning|pro tip|tip|note|remember|worked example|for example|scenario|example)\s*\*{0,2}\s*[:\-—]\s*(.+)$/i;

function calloutConfig(label) {
  const l = label.toLowerCase();
  if (l === 'exam tip') return { label: 'Exam Tip', color: 'var(--brand-amber)', bg: 'rgba(217,119,6,0.10)', icon: '🎯' };
  if (l === 'watch out' || l === 'caution' || l === 'warning') return { label: 'Watch Out', color: 'var(--brand-coral)', bg: 'rgba(220,38,38,0.08)', icon: '⚠️' };
  if (l === 'tip' || l === 'pro tip') return { label: 'Tip', color: 'var(--brand-emerald)', bg: 'rgba(5,150,105,0.08)', icon: '💡' };
  if (l === 'note' || l === 'remember') return { label: 'Note', color: 'var(--brand-slate)', bg: 'rgba(71,85,105,0.09)', icon: '📌' };
  return { label: l.includes('scenario') ? 'Scenario' : 'Worked Example', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', icon: '🧩' };
}

function Callout({ cfg, text }) {
  return (
    <div style={{ background: cfg.bg, borderLeft: `3px solid ${cfg.color}`, borderRadius: 10, padding: '11px 14px', margin: '8px 0' }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: cfg.color, margin: '0 0 5px' }}>
        <span aria-hidden>{cfg.icon}</span> {cfg.label}
      </p>
      <span style={{ fontSize: 13, color: C.creamText, lineHeight: 1.65 }}>{inlineFormat(text)}</span>
    </div>
  );
}

function MarkdownRenderer({ content, color }) {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trim = line.trim();
    if (/^---+$/.test(trim)) { elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid var(--border-medium)', margin: '10px 0' }}/>); i++; continue; }
    if (trim.startsWith('### ')) { elements.push(<h3 key={i} style={{ fontSize: 13, fontWeight: 800, color: C.creamText, margin: '10px 0 3px' }}>{inlineFormat(trim.slice(4))}</h3>); i++; continue; }
    if (trim.startsWith('## '))  { elements.push(<h2 key={i} style={{ fontSize: 15, fontWeight: 800, color: C.creamText, margin: '12px 0 5px' }}>{inlineFormat(trim.slice(3))}</h2>); i++; continue; }
    if (trim.startsWith('# '))   { elements.push(<h1 key={i} style={{ fontSize: 17, fontWeight: 900, color: C.creamText, margin: '14px 0 6px', letterSpacing: -0.3 }}>{inlineFormat(trim.slice(2))}</h1>); i++; continue; }
    if (trim.includes('|') && lines[i+1]?.includes('|') && lines[i+1]?.includes('-')) {
      const tb = [];
      while (i < lines.length && lines[i].includes('|')) { tb.push(lines[i]); i++; }
      elements.push(<MdTable key={`tb${i}`} lines={tb} color={color || '#2dd4bf'}/>);
      continue;
    }
    if (/^\d+\.\s/.test(trim)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        const m = lines[i].trim().match(/^(\d+)\.\s(.+)/);
        if (m) items.push({ n: m[1], t: m[2] });
        i++;
      }
      elements.push(
        <ol key={`ol${i}`} style={{ margin: '6px 0', padding: 0, listStyle: 'none' }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ display: 'flex', gap: 9, marginBottom: 5, alignItems: 'flex-start' }}>
              <span style={{ minWidth: 20, height: 20, borderRadius: '50%', background: `${color || '#2dd4bf'}25`, color: color || '#2dd4bf', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{item.n}</span>
              <span style={{ fontSize: 13, color: C.creamText, lineHeight: 1.65 }}>{inlineFormat(item.t)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }
    if (/^[-*•▸]\s/.test(trim)) {
      const items = [];
      while (i < lines.length && /^[-*•▸]\s/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^[-*•▸]\s/, '')); i++; }
      elements.push(
        <ul key={`ul${i}`} style={{ margin: '6px 0', padding: 0, listStyle: 'none' }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ display: 'flex', gap: 8, marginBottom: 3, alignItems: 'flex-start' }}>
              <span style={{ color: color || '#2dd4bf', fontSize: 11, marginTop: 3, flexShrink: 0 }}>▸</span>
              <span style={{ fontSize: 13, color: C.creamText, lineHeight: 1.65 }}>{inlineFormat(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }
    if (trim.startsWith('> ')) {
      elements.push(<blockquote key={i} style={{ borderLeft: `3px solid ${color || '#2dd4bf'}`, paddingLeft: 12, margin: '7px 0', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 13, lineHeight: 1.65 }}>{inlineFormat(trim.slice(2))}</blockquote>);
      i++; continue;
    }
    if (trim === '') { elements.push(<div key={i} style={{ height: 5 }}/>); i++; continue; }
    const cm = trim.match(CALLOUT_RE);
    if (cm) { elements.push(<Callout key={`co${i}`} cfg={calloutConfig(cm[1])} text={cm[2]} />); i++; continue; }
    elements.push(<p key={i} style={{ fontSize: 13, color: C.creamText, lineHeight: 1.75, margin: '2px 0' }}>{inlineFormat(line)}</p>);
    i++;
  }
  return <div>{elements}</div>;
}

// ─────────────────────────────────────────────
// MEMORY ORB
// ─────────────────────────────────────────────
function MemoryOrb({ loading, phase, small, color: colorOverride }) {
  const color = colorOverride || PHASE_META[phase]?.color || '#2dd4bf';
  const size  = small ? 28 : 34;
  const dot   = small ? 11 : 13;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: `${color}20`, border: `1.5px solid ${color}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: dot, height: dot, borderRadius: '50%', background: color, opacity: loading ? 0.4 : 0.9, boxShadow: loading ? 'none' : `0 0 8px ${color}`, animation: loading ? 'orbP 1s ease-in-out infinite' : 'orbI 3s ease-in-out infinite' }}/>
      <style>{`@keyframes orbI{0%,100%{opacity:.8;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}@keyframes orbP{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:.7;transform:scale(1.05)}}`}</style>
    </div>
  );
}

// Persistent M[Emory] presence — one tutor entity that reflects the engine's
// live state (teaching / thinking / checking / repairing). Reinforces
// "adaptive system" over "chatbot" without a speech bubble.
// Rotating wait sub-lines so the pause reads as M[Emory] working on something
// specific, not a generic spinner. Rotated by turn count, not random.
const WAIT_QUESTION = ['Working out a clear answer…', 'Thinking that through…', 'Finding the simplest way to explain this…'];
const WAIT_CONTINUE = ['Lining up the next concept…', 'Reading your signals…', 'Adapting to how you learn…'];

function MemoryPresence({ engine, chatHistory = [] }) {
  const phase = engine.sessionPhase;
  let label, sub, color, live = false;
  if (engine.loading) {
    // Contextual wait copy — what M[Emory] is actually doing this turn.
    const lastUser = [...chatHistory].reverse().find(m => m.role === 'user')?.content || '';
    const isQuestion = /\?\s*$/.test(lastUser) || /^(what|why|how|when|where|which|can|does|is|are|do)\b/i.test(lastUser.trim());
    const opening = chatHistory.filter(m => m.role === 'assistant').length === 0;
    color = PHASE_META[phase]?.color || '#2dd4bf'; live = true;
    if (opening) {
      label = 'Getting started'; sub = 'Setting up your lesson…';
    } else if (phase === 'CHECKPOINT' || engine.quiz) {
      label = 'Preparing'; sub = 'Putting your check together…';
    } else if (isQuestion) {
      label = 'Thinking'; sub = WAIT_QUESTION[chatHistory.length % WAIT_QUESTION.length];
    } else {
      label = 'Thinking'; sub = WAIT_CONTINUE[chatHistory.length % WAIT_CONTINUE.length];
    }
  } else if (engine.practice) {
    label = 'Checking your understanding'; sub = 'A quick practice beat'; color = PHASE_META.CHECKPOINT.color;
  } else if (engine.quiz) {
    label = 'Checking your mastery'; sub = 'Scoring this checkpoint'; color = PHASE_META.CHECKPOINT.color;
  } else if (engine.primaryAction === 'REPAIRING' || phase === 'REMEDIATION') {
    label = 'Repairing the gap'; sub = 'Re-teaching what slipped'; color = PHASE_META.REMEDIATION.color; live = engine.primaryAction === 'REPAIRING';
  } else if (engine.lessonComplete) {
    label = 'Lesson complete'; sub = 'Nice work'; color = PHASE_META.VALIDATION.color;
  } else {
    label = 'Teaching'; sub = 'Adapting to how you learn'; color = PHASE_META.LESSON.color;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 12px', borderRadius: 12, background: C.subtle, border: `1px solid ${C.border}`, marginBottom: 10, flexShrink: 0 }}>
      <MemoryOrb phase={phase} loading={live} color={color} />
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.3, margin: 0, color, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          M[Emory]
          <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>· {label}</span>
        </p>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '1px 0 0' }}>{sub}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PHASE PIP
// ─────────────────────────────────────────────
function PhasePip({ phase, currentPhase }) {
  const phases = ['PROFILE','LESSON','CHECKPOINT','REMEDIATION','VALIDATION'];
  const meta   = PHASE_META[phase] || { color: 'var(--accent-primary)' };
  const idx    = phases.indexOf(phase);
  const cur    = phases.indexOf(currentPhase);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <div style={{ width: idx === cur ? 8 : 5, height: idx === cur ? 8 : 5, borderRadius: '50%', background: idx < cur ? meta.color : idx === cur ? meta.color : 'var(--text-muted)', boxShadow: idx === cur ? `0 0 7px ${meta.color}` : 'none', transition: 'all 0.3s', flexShrink: 0 }}/>
      {idx < phases.length - 1 && <div style={{ width: 10, height: 1.5, borderRadius: 99, background: idx < cur ? meta.color : 'var(--border-medium)', transition: 'background 0.3s' }}/>}
    </div>
  );
}

// ─────────────────────────────────────────────
// MASTERY BADGE
// ─────────────────────────────────────────────
function MasteryBadge({ state }) {
  const cfg = {
    mastered:   { label: 'Mastered',   bg: 'rgba(52,211,153,0.15)', color: 'var(--progress-strong)' },
    understood: { label: 'Understood', bg: 'var(--border-medium)', color: 'var(--text-secondary)' },
    struggling: { label: 'Struggling', bg: 'rgba(248,113,113,0.15)', color: 'var(--progress-weak)' },
  }[state] || { label: 'Learning', bg: 'rgba(45,212,191,0.15)', color: 'var(--accent-primary)' };
  return <span style={{ padding: '2px 9px', borderRadius: 99, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, letterSpacing: 0.3 }}>{cfg.label}</span>;
}

// ─────────────────────────────────────────────
// VIDEO PLAYER — plain YouTube embed iframe. The IFrame Player API approach (used
// to force 1.5x) replaced our host node with its own iframe, conflicting with
// React's DOM ownership → blank player. A standard embed iframe renders reliably.
// (1.5x is dropped; it requires the IFrame API. Autoplay via the URL param + the
// allow="autoplay" attribute — the learner's "Take a Break" click is the gesture
// that permits it.)
// ─────────────────────────────────────────────
function VideoFrame({ videoId, title, fillHeight, isMobile }) {
  if (!videoId) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://adaptos.app';
  const src = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1&playsinline=1&origin=${encodeURIComponent(origin)}`;
  // iframe onError rarely fires for cross-origin YouTube embeds (a failed video
  // surfaces inside YouTube's own player UI, not as a frame load error), but wire
  // it anyway so any real load failure is visible in the console.
  const onError = (e) => {
    // eslint-disable-next-line no-console
    console.error('[VideoFrame] YouTube embed failed to load', { videoId, src, error: e });
  };
  // fillHeight stretches to fill a fixed-height parent (the desktop split panel).
  // On mobile the panel is auto-height, so flex:1/height:100% collapse to 0 and
  // the iframe renders blank. Use a responsive 16:9 container with a 220px floor
  // instead — full container width, always visible (shows YouTube's thumbnail
  // state before the video loads), and never clipped by the surrounding layout.
  const useFill = fillHeight && !isMobile;
  return (
    <div style={useFill
      ? { flex: 1, minHeight: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}` }
      : { position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, minHeight: 220, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}` }}>
      <iframe
        src={src}
        title={title || 'Video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onError={onError}
        style={useFill
          ? { width: '100%', height: '100%', border: 'none', display: 'block' }
          : { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// YOUTUBE EMBED — resolves a search query → videoId, then renders VideoFrame
// (1.5x + autoplay). Keeps the search + graceful "watch on YouTube" fallbacks.
// ─────────────────────────────────────────────
function YouTubeEmbed({ query, fillHeight, autoplay = false, isMobile, lessonData = null, trackData = null }) {
  const [videoId,    setVideoId]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const [noneFound,  setNoneFound]  = useState(false);
  useEffect(() => {
    if (!query) return;
    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
    if (!apiKey) { setError(true); return; }
    let cancelled = false;
    setLoading(true); setVideoId(null); setError(false); setEmbedError(false); setNoneFound(false);

    // Path 3 hardening (model-emitted [VIDEO:] query). Curated the same way the
    // lesson video (Path 1) is: entertainment denylist, a 6-minute duration cap
    // (matching the learner-triggered break video, Path 2), then the LLM re-rank
    // pass. If the re-ranker returns NONE we surface NOTHING — a missing video is
    // better than a wrong one. We never fall back to an off-topic first result.
    (async () => {
      try {
        const sr = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&key=${apiKey}&type=video&videoEmbeddable=true&maxResults=10&relevanceLanguage=en`).then(r => r.json());
        const snippetById = new Map();
        (sr?.items || []).forEach(it => {
          const vid     = it.id?.videoId;
          const title   = it.snippet?.title || '';
          const channel = it.snippet?.channelTitle || '';
          if (!vid) return;
          if (isEntertainmentHit({ title, channel })) return;
          snippetById.set(vid, { title, channel });
        });
        const ids = [...snippetById.keys()];
        if (!ids.length) { if (!cancelled) { setNoneFound(true); setLoading(false); } return; }

        // Duration filter — ≤360s (6 min), matching Path 2.
        const dr = await fetch(`https://www.googleapis.com/youtube/v3/videos?id=${ids.join(',')}&part=contentDetails&key=${apiKey}`).then(r => r.json());
        const parseDur = (iso) => {
          const m = (iso || '').match(/PT(?:(\d+)M)?(?:(\d+)S)?/);
          return (parseInt(m?.[1] || '0', 10) * 60) + parseInt(m?.[2] || '0', 10);
        };
        const detailsById = new Map((dr?.items || []).map(it => [it.id, it]));
        const eligible = ids
          .map(id => {
            const sec  = parseDur(detailsById.get(id)?.contentDetails?.duration || '');
            if (!(sec > 0 && sec <= 360)) return null;
            const snip = snippetById.get(id) || {};
            return { id, title: snip.title || '', channel: snip.channel || '', duration_seconds: sec };
          })
          .filter(Boolean);
        if (!eligible.length) { if (!cancelled) { setNoneFound(true); setLoading(false); } return; }

        // LLM re-rank. With ≥2 candidates, trust the verdict: a null winner (the
        // re-ranker said NONE, or none validated against the set) means show no
        // video on Path 3 — never fall back to an off-topic first result. A lone
        // eligible candidate can't be re-ranked (endpoint needs ≥2) but already
        // passed the denylist + duration gate on a concept-anchored query.
        let chosenId;
        if (eligible.length >= 2) {
          const winner = await rerankCandidates({
            lessonTitle:      lessonData?.title || query,
            lessonObjectives: lessonData?.objectives || [],
            trackLabel:       trackData?.label || '',
            candidates:       eligible,
          });
          if (cancelled) return;
          if (winner && eligible.some(e => e.id === winner)) chosenId = winner;
          else { setNoneFound(true); setLoading(false); return; }   // NONE → silent fail
        } else {
          chosenId = eligible[0].id;
        }
        if (!cancelled) { setVideoId(chosenId); setLoading(false); }
      } catch {
        if (!cancelled) { setError(true); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [query, lessonData, trackData]);
  if (!query) return null;
  if (loading) return <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${C.border}`, borderRadius: 8 }}><p style={{ fontSize: 11, color: C.muted, margin: 0 }}>Loading video...</p></div>;

  // Re-ranker said NONE (or nothing cleared the denylist/duration/relevance bar):
  // surface no video. A missing video is better than a wrong one — neutral line,
  // no promotional "watch on YouTube" fallback.
  if (noneFound) return (
    <div style={{ flex: 1, minHeight: isMobile ? 120 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${C.border}`, borderRadius: 8 }}>
      <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', margin: 0, lineHeight: 1.5 }}>No relevant short video for this concept. Use the reference card for now.</p>
    </div>
  );

  // YouTube API returned no result — original "Watch on YouTube" link
  if (error || !videoId) return (
    <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`} target="_blank" rel="noopener noreferrer"
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 11, background: 'rgba(255,0,0,0.08)', border: '1px solid rgba(255,0,0,0.2)', textDecoration: 'none' }}>
      <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: '#FF0000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
      </div>
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1px' }}>Watch: {query}</p>
        <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>Opens YouTube · new tab</p>
      </div>
    </a>
  );

  // Embed failed to load — clean fallback per spec
  if (embedError) return (
    <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${query} CompTIA A+`)}`} target="_blank" rel="noopener noreferrer"
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 11, background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', textDecoration: 'none' }}>
      <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: '#FF0000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
      </div>
      <div>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 2px' }}>Video unavailable on this device.</p>
        <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>Search "{query} CompTIA A+" on YouTube · new tab</p>
      </div>
    </a>
  );

  // Resolved a videoId — hand off to the IFrame player (1.5x + autoplay).
  return <VideoFrame videoId={videoId} title={query} fillHeight={fillHeight} isMobile={isMobile} />;
}

// ─────────────────────────────────────────────
// CHAT INPUT
// ─────────────────────────────────────────────
function ChatInput({ onSend, loading, phase, placeholder = 'Type a message…', onInteract }) {
  const color = PHASE_META[phase]?.color || '#2dd4bf';
  const [val, setVal] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (!loading && window.innerWidth >= 768) ref.current?.focus(); }, [loading]);
  const submit = () => { if (!val.trim() || loading) return; onSend(val.trim()); setVal(''); };
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input ref={ref} value={val} onChange={e => { onInteract?.(); setVal(e.target.value); }} onKeyDown={e => e.key === 'Enter' && submit()}
        placeholder={placeholder} disabled={loading}
        style={{ flex: 1, padding: '11px 14px', borderRadius: 10, background: C.subtle, border: `1.5px solid ${val.trim() ? `${color}66` : C.border}`, color: 'var(--text-primary)', fontSize: 16, outline: 'none', fontFamily: 'inherit', opacity: loading ? 0.6 : 1, transition: 'border-color 0.2s' }}
      />
      <button onClick={submit} disabled={loading || !val.trim()}
        style={{ padding: '11px 18px', borderRadius: 10, minWidth: 64, border: 'none', background: val.trim() && !loading ? color : 'var(--bg-elevated)', color: val.trim() && !loading ? 'var(--accent-text)' : 'var(--text-muted)', fontSize: 13, fontWeight: 700, cursor: val.trim() && !loading ? 'pointer' : 'default', fontFamily: 'inherit', transition: 'all 0.2s' }}>Send</button>
    </div>
  );
}

// ─────────────────────────────────────────────
// RIGHT PANEL
// ─────────────────────────────────────────────
function RightPanel({ isMobile, reference, video, autoVideoSignal, lessonData, trackData, sessionPhase, roadmapProgress, onSend, breakVideo, breakVideoLoading, breakVideoError, requestBreakVideo, clearBreakVideo, breakAllowed, currentFocus, learner, lastNote }) {
  const [notes,      setNotes]      = useState('');
  const [tab,        setTab]        = useState('reference'); // reference | video | notes
  const [autoplayVideo, setAutoplayVideo] = useState(false);
  const [savedNotes, setSavedNotes] = useState([]);

  // Model-flagged visual break: when the engine emits a fresh [VIDEO:] tag
  // (autoVideoSignal bumps), auto-switch the panel to the Video tab and let it
  // autoplay — a curated visual break from the text, per the model's cue.
  const lastAutoVideoRef = useRef(0);
  useEffect(() => {
    if (autoVideoSignal && autoVideoSignal !== lastAutoVideoRef.current) {
      lastAutoVideoRef.current = autoVideoSignal;
      setTab('video');
      setAutoplayVideo(true);
    }
  }, [autoVideoSignal]);

  // Learner-triggered "Take a Break" video also belongs in the Video tab (not
  // inline in Adaptive Reference) — switch to it as soon as it's requested.
  useEffect(() => {
    if (breakVideoLoading || breakVideo) setTab('video');
  }, [breakVideoLoading, breakVideo]);

  // Auto-saved notes scoped to THIS lesson. Reloaded whenever a new note is saved
  // (lastNote changes) so the just-saved shortcut/trap appears without a refresh.
  useEffect(() => {
    if (!learner?.id || !lessonData?.id) { setSavedNotes([]); return undefined; }
    let cancelled = false;
    getNotes(learner.id, 100).then(rows => {
      if (!cancelled) setSavedNotes((rows || []).filter(n => n.lesson_id === lessonData.id));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [learner?.id, lessonData?.id, lastNote]);
  const objectives = lessonData?.objectives || [];
  const color = PHASE_META[sessionPhase]?.color || '#2dd4bf';
  const card = { background: 'var(--bg-elevated)', border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
  const lbl  = { fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px', flexShrink: 0 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: isMobile ? 'auto' : '100%' }}>

      {/* Tab bar — amber underline on the active tab (section 8) */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        {[['reference', 'Adaptive Reference'], ['video', 'Video'], ['notes', 'Learning Notes']].map(([id, label]) => {
          const on = tab === id;
          return (
            <button key={id} type="button" onClick={() => { setTab(id); setAutoplayVideo(false); }} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              padding: '8px 10px', fontSize: 12, fontWeight: on ? 700 : 600,
              color: on ? 'var(--text-primary)' : C.muted,
              borderBottom: `2px solid ${on ? 'var(--brand-amber)' : 'transparent'}`,
              marginBottom: -1,
            }}>{label}</button>
          );
        })}
      </div>

      {tab === 'reference' && (<>
      {/* Learning State — compact at top; tightened to cede vertical space to
          the Adaptive Reference below (flex 1). */}
      <div style={{ ...card, flex: '0 0 auto', padding: '9px 11px' }}>
        <p style={{ ...lbl, margin: '0 0 5px' }}>Learning State</p>
        <div>
          {trackData && <p style={{ fontSize: 11, color, fontWeight: 700, margin: '0 0 3px' }}>{trackData.label}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 80 }}>Phase</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{{ PROFILE: 'Onboarding', LESSON: 'Active Lesson', CHECKPOINT: 'Checkpoint', REMEDIATION: 'Repair', VALIDATION: 'Complete' }[sessionPhase] || 'Active Lesson'}</span>
            </div>
            {(currentFocus || lessonData?.title) && (
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 80 }}>Focus</span>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{currentFocus || lessonData.title}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 80 }}>Next</span>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>{{ LESSON: 'Practice checkpoint', CHECKPOINT: 'Clear the mastery gate', REMEDIATION: 'Repair detected gap', VALIDATION: 'Next lesson' }[sessionPhase] || 'Continue'}</span>
            </div>
          </div>
          {roadmapProgress && (
            <ProgressBar value={roadmapProgress.completed_count} max={roadmapProgress.total_count} color={color} label={`Track Progress · ${roadmapProgress.completed_count} of ${roadmapProgress.total_count} lessons`}/>
          )}
          {objectives.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 5 }}>
              <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 1 }}>Lesson Objectives</span>
              {objectives.map((obj, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <span style={{ color, fontSize: 11, flexShrink: 0, marginTop: 2 }}>▸</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>{obj}</span>
                </div>
              ))}
            </div>
          )}
          {!lessonData && <p style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>Starting your learning journey...</p>}
        </div>
      </div>

      {/* Adaptive Reference — fills all remaining space. Priority: structured
          reference markdown > video > placeholder. Reference wins when both exist
          (reused MarkdownRenderer; no new renderer, no toggle — see patch notes). */}
      <div style={{ ...card, flex: isMobile ? '0 0 auto' : '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <p style={lbl}>Adaptive Reference</p>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: isMobile ? 'visible' : 'auto' }}>
          {reference ? (
            <>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>
                {detectReferenceLabel(reference)}
              </p>
              <MarkdownRenderer content={reference} color={color}/>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed var(--border-soft)`, borderRadius: 8 }}>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>Reference material will appear here when relevant.</p>
            </div>
          )}
        </div>
        {/* Take a Break From Reading — learner-triggered ≤6 min YouTube clip on
            the current concept. Lives below the reference (does not replace it).
            Hidden during quiz/practice via breakAllowed. No autoplay; loads the
            embed, learner clicks play. Degrades to a clean "no short video found"
            line on failure. breakVideo is an object: { id, title, duration }. */}
        {breakAllowed && (
          <div style={{ flexShrink: 0, marginTop: 8 }}>
            {breakVideo ? (
              // Video plays in the Video tab now (not inline here). Point there.
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 14px', borderRadius: 10, background: C.subtle, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, lineHeight: 1.4 }}>▶ Now playing in the Video tab — {breakVideo.title}</span>
                <button onClick={clearBreakVideo} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontWeight: 700, flexShrink: 0 }}>Close</button>
              </div>
            ) : breakVideoLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '11px', borderRadius: 10, background: C.subtle, border: `1px solid ${C.border}` }}>
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  {[0,1,2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, animation: `dot 1.2s ease ${i*0.2}s infinite`, opacity: 0.4 }}/>)}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Finding a short video under 6 minutes…</span>
              </div>
            ) : breakVideoError ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 14px', borderRadius: 10, background: C.subtle, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>No short video found for this concept. Use the reference card for now.</span>
                <button onClick={clearBreakVideo} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontWeight: 600, flexShrink: 0 }}>Dismiss</button>
              </div>
            ) : (
              <>
                <button
                  className="lesson-btn"
                  onClick={() => requestBreakVideo?.()}
                  style={{ width: '100%', background: C.teal, borderColor: C.teal, color: C.tealText, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <span style={{ fontSize: 14 }}>▶</span>
                  Take a Break From Reading →
                </button>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0', textAlign: 'center', fontStyle: 'italic' }}>
                  Watch a short video on this concept.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      </>)}

      {/* Video tab — break video (learner-triggered, curated) takes priority,
          then the model-pinned lesson video. All play via VideoFrame (1.5x +
          autoplay). */}
      {tab === 'video' && (
        // On mobile the panel is auto-height: flex:1 1 0 would give the card a 0
        // height and overflow:hidden would clip the player away. Switch to
        // content-height with visible overflow so the iframe (and placeholders)
        // stay visible; desktop keeps the fill-the-split behavior.
        <div style={{ ...card, flex: isMobile ? '0 0 auto' : '1 1 0', minHeight: 0, overflow: isMobile ? 'visible' : 'hidden' }}>
          <p style={lbl}>Video</p>
          {breakVideo ? (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 8 }}>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, margin: 0, flexShrink: 0, lineHeight: 1.4 }}>{breakVideo.duration} · {breakVideo.title}</p>
              <VideoFrame videoId={breakVideo.id} title={breakVideo.title} fillHeight isMobile={isMobile} />
              <button className="lesson-btn" onClick={clearBreakVideo} style={{ alignSelf: 'flex-end', background: C.teal, borderColor: C.teal, color: C.tealText, fontSize: 11, padding: '5px 12px', flexShrink: 0 }}>Close</button>
            </div>
          ) : breakVideoLoading ? (
            <div style={{ flex: 1, minHeight: isMobile ? 120 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
              <span style={{ display: 'inline-flex', gap: 4 }}>{[0,1,2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: C.teal, animation: `dot 1.2s ease ${i*0.2}s infinite`, opacity: 0.4 }}/>)}</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Finding a short video…</span>
            </div>
          ) : breakVideoError ? (
            <div style={{ flex: 1, minHeight: isMobile ? 120 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${C.border}`, borderRadius: 8 }}><p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>No short video found for this concept.</p></div>
          ) : video ? (
            <YouTubeEmbed query={video} fillHeight isMobile={isMobile} lessonData={lessonData} trackData={trackData}/>
          ) : (
            <div style={{ flex: 1, minHeight: isMobile ? 120 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${C.border}`, borderRadius: 8 }}><p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>No video yet — tap “Take a Break From Reading” in the Adaptive Reference tab.</p></div>
          )}
        </div>
      )}

      {/* Learning Notes — auto-saved notes for THIS lesson + a scratch pad */}
      {tab === 'notes' && (
      <div style={{ ...card, flex: '1 1 0', minHeight: 0, overflowY: 'auto' }}>
        <p style={lbl}>Saved For This Lesson</p>
        {savedNotes.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14, flexShrink: 0 }}>
            {savedNotes.map(n => (
              <div key={n.id} style={{ background: 'var(--bg-card)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 11px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                  {n.source === 'manual' ? 'Your note' : 'Auto-saved'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{n.content}</div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', margin: '0 0 14px', flexShrink: 0 }}>
            Shortcuts, exam traps, and key distinctions auto-save here as you study this lesson.
          </p>
        )}
        <p style={lbl}>Scratch Pad</p>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Jot your own notes…"
          style={{ minHeight: 90, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', outline: 'none', color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.7, fontFamily: 'inherit', resize: 'vertical' }}
        />
        {notes && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, flexShrink: 0 }}>
            <button onClick={() => navigator.clipboard?.writeText(notes)} style={{ padding: '3px 9px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Copy</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN — STUDY CONSOLE
// ─────────────────────────────────────────────
// Adzuna search term per track — drives the nav job ticker.
const TRACK_JOBS = {
  comptia_aplus:    'IT Support Technician',
  comptia_network:  'Network Technician',
  comptia_security: 'Security Analyst',
  az_900:           'Azure Cloud',
  az_104:           'Azure Administrator',
  ab_900:           'Microsoft 365 Administrator',
  ms_102:           'Microsoft 365 Administrator',
};

// Adaptive primary-action labels — one renders at a time (engine.primaryAction).
const PRIMARY_LABEL = {
  RETAKE_QUIZ:         'Ready to Retake Quiz →',
  CHECK_UNDERSTANDING: 'Check My Understanding',
  CONTINUE_LESSON:     'Continue Lesson',
};

// State-aware text-input placeholders — driven by the same primaryAction the
// primary button reads, so the placeholder and the button are always in sync.
const PLACEHOLDER_BY_ACTION = {
  RETAKE_QUIZ:         'Ask for clarity before your retake…',
  CHECK_UNDERSTANDING: 'Ready to check your understanding…',
  CONTINUE_LESSON:     'Ask a question or continue when ready…',
  REPAIRING:           "Ask for help or describe what's unclear…",
};

// Adaptive Reference subheading — picked from a few cheap content cues.
// Most specific first so e.g. a symptom table doesn't fall through to "Quick Comparison".
function detectReferenceLabel(md) {
  if (!md) return 'Concept Reference';
  const lower = md.toLowerCase();
  if (/\b(symptom|cause|fix|likely cause|first fix)\b/.test(lower) && md.includes('|')) return 'Symptom Guide';
  if (/\*\*[^*]+\*\*\s*[|·:]\s*\*\*[^*]+\*\*/.test(md))                                 return 'Key Differences';
  if (md.includes('|'))                                                                  return 'Quick Comparison';
  return 'Concept Reference';
}

// Pull a "Key Takeaway" block out of an assistant message so it can render as a
// separate dark-amber card (section 8). Matches a "**Key Takeaway**" or
// "Key Takeaway:" header and captures to the next paragraph break or end.
// Returns { body, takeaway } — takeaway is null when the pattern is absent.
function extractKeyTakeaway(content) {
  if (!content) return { body: content, takeaway: null };
  const rx = /(?:^|\n)\s*(?:\*\*\s*Key Takeaway\s*\*\*|Key Takeaway)\s*[:：]?\s*/i;
  const m = content.match(rx);
  if (!m) return { body: content, takeaway: null };
  const after = content.slice(m.index + m[0].length);
  const stop = after.search(/\n\s*\n/);
  const takeaway = (stop === -1 ? after : after.slice(0, stop)).trim();
  if (!takeaway) return { body: content, takeaway: null };
  return { body: content.slice(0, m.index).trim(), takeaway };
}

// Assistant lesson turn — renders the body in the existing cream card and any
// extracted Key Takeaway as a separate dark-amber card below it (not inline).
function AssistantMessage({ content, phase }) {
  const { body, takeaway } = extractKeyTakeaway(content);
  const color = PHASE_META[phase]?.color || '#2dd4bf';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <MemoryOrb loading={false} phase={phase || 'LESSON'} small/>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {body && (
          <div style={{ background: C.cream, borderRadius: 12, borderTopLeftRadius: 4, padding: '12px 14px' }}>
            <MarkdownRenderer content={body} color={color}/>
          </div>
        )}
        {takeaway && (
          <div style={{ background: 'var(--surface-amber-dark)', borderRadius: 12, padding: '14px 16px' }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 800, color: 'var(--surface-amber-cream)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <span aria-hidden>💡</span> Key Takeaway
            </p>
            <p style={{ fontSize: 14, color: 'var(--surface-amber-cream)', lineHeight: 1.6, margin: 0 }}>{takeaway}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// "Why This Step" — persistent dark panel above the input bar, shown only when a
// gap exists during LESSON or REMEDIATION (section 8). Replaces the rotating hint.
function WhyThisStep({ remediationActive, repairDelivered, currentFocus, failures }) {
  // Repair-initiation announcement: appears the moment Repair begins, then goes
  // away once the first repair turn has rendered its teaching (repairDelivered).
  // It no longer rides along on every lesson/repair turn shrinking the learner area.
  const show = remediationActive && !repairDelivered && failures > 0 && currentFocus;
  if (!show) return null;
  return (
    <div style={{ background: 'var(--surface-dark)', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, marginTop: 4 }}>
      <span style={{ color: 'var(--brand-coral)', fontSize: 18, flexShrink: 0 }} aria-hidden>🎯</span>
      <p style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#fff', margin: 0, lineHeight: 1.5 }}>
        You&apos;ve slipped on <strong>{currentFocus}</strong> in recent questions — let&apos;s strengthen it before moving on.
      </p>
      <span style={{ flexShrink: 0, background: 'rgba(220,38,38,0.15)', color: 'var(--brand-coral)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 99, padding: '5px 12px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
        Your Recent Gap — {currentFocus} — {failures} of last 3 incorrect
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────
// ── Active Concept Canvas ────────────────────────────────────────
// The current teaching turn renders as a focused, full-width lesson panel
// (not a chat bubble). Prior turns fold into a collapsible transcript so the
// surface reads as a lesson you move through, not an endless chat log.

// Mirror of the engine's question heuristic (useLearningEngine.send → LEARNER_QUESTION).
// Used to decide whether the learner turn that prompted the focal answer stays
// visible directly above it (a question) instead of folding into the collapsed
// transcript — so a question reads together with its answer.
function isLearnerQuestion(text) {
  if (!text) return false;
  const q = text.trim();
  return q.endsWith('?') ||
    /^(what|why|how|when|where|which|who|does|can|is|are|will|should)\b/i.test(q);
}

// Learner turns read as a quiet "You asked: …" transcript line above the
// answer — not a chat balloon — so the surface stays a lesson, not a chat log.
function UserBubble({ content }) {
  return (
    <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0, padding: '2px 0' }}>
      <span style={{ fontWeight: 700 }}>You asked:</span> {content}
    </p>
  );
}

// ── Typed lesson blocks (Phase 2) ────────────────────────────────
// Parser-extracted artifacts render as distinct, styled blocks inside the
// canvas in a fixed canonical beat order — not document order. Each renders
// only when its field is present; absent fields are a clean no-op. The
// markdown-callout detection inside MarkdownRenderer stays as the fail-closed
// fallback for prose the model emits without a typed tag.

// DIAGRAM (Phase 3) — model-emitted inline SVG, sanitized through an allowlist
// at render time and themed via the phase color. Fail-closed: when the SVG does
// not survive sanitization (sanitizeSvg → null) the block renders nothing — the
// raw string is NEVER injected. Gated upstream to visual concepts by block affinity.
function DiagramBlock({ svg, color }) {
  const clean = useMemo(() => sanitizeSvg(svg), [svg]);
  if (!clean) return null;
  return (
    <div style={{ background: C.subtle, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 800, color, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <span aria-hidden>📐</span> Diagram
      </p>
      <div
        style={{ color, maxWidth: '100%', overflowX: 'auto' }}
        // Sanitized by sanitizeSvg (allowlist + fail-closed) immediately above.
        // currentColor strokes/fills inherit the phase theme color set here.
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    </div>
  );
}

// EXAMPLE — purple "Real-world" worked-example card. Body is markdown.
function ExampleBlock({ content, color }) {
  if (!content) return null;
  return (
    <div style={{ background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.22)', borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 800, color: '#7c3aed', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <span aria-hidden>🧩</span> Real-World Example
      </p>
      <MarkdownRenderer content={content} color={color}/>
    </div>
  );
}

// KEY TAKEAWAY — dark-amber callout. Reuses the established ConceptCanvas style.
function KeyTakeawayBlock({ text }) {
  if (!text) return null;
  return (
    <div style={{ background: 'var(--surface-amber-dark)', borderRadius: 12, padding: '14px 16px', marginTop: 14 }}>
      <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 800, color: 'var(--surface-amber-cream)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <span aria-hidden>💡</span> Key Takeaway
      </p>
      <p style={{ fontSize: 14, color: 'var(--surface-amber-cream)', lineHeight: 1.6, margin: 0 }}>{text}</p>
    </div>
  );
}

// MASTERY SIGNAL — emerald progress acknowledgement, one sentence.
function MasterySignalBlock({ text }) {
  if (!text) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'rgba(5,150,105,0.09)', border: '1px solid rgba(5,150,105,0.25)', borderRadius: 12, padding: '12px 16px', marginTop: 14 }}>
      <span aria-hidden style={{ fontSize: 16, flexShrink: 0 }}>✅</span>
      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-emerald)', lineHeight: 1.55, margin: 0 }}>{inlineFormat(text)}</p>
    </div>
  );
}

// NEXT STEP — slate bridge line to what comes after this concept.
function NextStepBlock({ text }) {
  if (!text) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
      <span aria-hidden style={{ fontSize: 13, color: C.muted, flexShrink: 0 }}>→</span>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
        <span style={{ fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11, marginRight: 6 }}>Next</span>
        {inlineFormat(text)}
      </p>
    </div>
  );
}

function ConceptCanvas({ content, phase, diagram, example, masterySignal, nextStep }) {
  const { body, takeaway } = extractKeyTakeaway(content);
  const color      = PHASE_META[phase]?.color || '#2dd4bf';
  const phaseLabel = PHASE_META[phase]?.label || 'Lesson';
  return (
    <div style={{ background: C.cream, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 24px', boxShadow: 'var(--shadow-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color }}>{phaseLabel}</span>
      </div>
      {/* Canonical beat order: explanation+inline-table → diagram → example → key
          takeaway → mastery signal → next step. (Practice/checkpoint preempt the
          canvas entirely upstream, so they need no slot here.) */}
      {body && <MarkdownRenderer content={body} color={color}/>}
      <DiagramBlock svg={diagram} color={color}/>
      <ExampleBlock content={example} color={color}/>
      <KeyTakeawayBlock text={takeaway}/>
      <MasterySignalBlock text={masterySignal}/>
      <NextStepBlock text={nextStep}/>
    </div>
  );
}

function EarlierTranscript({ messages }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span> Earlier in this lesson ({messages.length})
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: 0.9, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
          {messages.map((msg, idx) => (
            msg.role === 'user'
              ? <UserBubble key={idx} content={msg.content} />
              : <AssistantMessage key={idx} content={msg.content} phase={msg.phase} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function StudyConsole({ learner, trackId, onSignOut, theme, toggleTheme, embedded = false, onExit }) {
  const trackData = TRACKS[trackId] || TRACKS.comptia_aplus;
  const engine    = useLearningEngine({ learner, trackId });
  const [chatHistory,     setChatHistory]     = useState([]);
  const [lastDisplay,     setLastDisplay]     = useState('');
  const [roadmapProgress, setRoadmapProgress] = useState(null);
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < 768);
  const [showPanel,       setShowPanel]       = useState(false);
  const [jobs,            setJobs]            = useState([]);
  const [scLearnerState,  setScLearnerState]  = useState(null);  // read-only: powers the Why This Step gap pill
  const chatBottomRef = useRef(null);
  const canvasRef     = useRef(null);
  const actionRef     = useRef(null);  // the bottom action footer (practice / quiz / CTA + input)
  const timerRef      = useRef(null);  // setTimeout handle for the silent auto-advance
  // Ref-guarded init — synchronous and survives every re-render path that doesn't
  // unmount the component. The previous `started` useState was async; a fast
  // re-render in the same effect tick could still see started===false and fire
  // startMission a second time.
  const hasInitialized = useRef(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (!learner?.id) return;
    hasInitialized.current = true;
    engine.startMission();
  }, [learner?.id]);
  useEffect(() => { return () => engine.endCurrentSession(); }, []);

  // First-run activation: 30s+ in the console on Lesson 1 counts as engagement.
  // A 3–4s open-and-leave does NOT — navigating away unmounts StudyConsole, whose
  // cleanup clears this timer before it fires. The engine no-ops off Lesson 1 and
  // de-dupes via the write-once DB stamp, so re-arming per lesson is harmless.
  useEffect(() => {
    if (engine.lessonData?.sequence_order !== 1) return undefined;
    const t = setTimeout(() => engine.engageLesson1('dwell_30s'), 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.lessonData?.id, engine.lessonData?.sequence_order]);

  useEffect(() => {
    if (!learner?.id || !trackId) return;
    // Clear the prior lesson's track-progress value the moment the lesson changes so
    // the panel shows a neutral/loading state instead of carrying forward a stale
    // count on the new lesson's first turn; refetch the fresh value immediately.
    setRoadmapProgress(null);
    fetchRoadmapProgress(learner.id, trackId).then(setRoadmapProgress).catch(() => {});
  }, [learner?.id, trackId, engine.lessonData?.id]);

  useEffect(() => {
    if (!engine.display || engine.display === lastDisplay) return;
    setLastDisplay(engine.display);
    // Note-only / near-empty turns: when a NOTE artifact was extracted this turn
    // (engine.lastNote is set briefly) and the leftover display is ≤15 words,
    // suppress the chat card. The transient "Saved to Learning Notes" inline
    // below the scroll acknowledges it.
    const wc = engine.display.trim().split(/\s+/).filter(Boolean).length;
    if (wc <= 15 && engine.lastNote) return;
    // Typed lesson blocks (Phase 2) — attach the turn's parser-extracted artifacts
    // to the history entry so the canvas renders them with their own turn, not the
    // latest live signals. Fields are render-only; absent ones simply don't render.
    const s = engine.signals || {};
    setChatHistory(prev => [...prev, {
      role: 'assistant',
      content: engine.display,
      video: engine.video,
      phase: engine.sessionPhase,
      diagram: s.diagram || null,
      example: s.example || null,
      masterySignal: s.masterySignal || null,
      nextStep: s.nextStep || null,
    }]);
  }, [engine.display]);

  // New teaching turn → scroll to the TOP of the canvas (read from the start);
  // while loading, follow the bottom so the typing indicator stays in view.
  //
  // Mobile exception: desktop pins the action footer (practice/quiz Submit +
  // Continue) as an always-visible flexShrink:0 region beside a scrolling chat.
  // Mobile collapses chat + footer into one scroll flow, so scrolling the canvas
  // to its top pushes the interactive surface below the fold / behind the fixed
  // BottomNav — leaving no way to submit or continue. When such a surface is
  // active, bring the footer into view instead.
  useEffect(() => {
    const interactiveSurface =
      engine.practice || engine.quiz || engine.retryState?.active ||
      engine.lessonComplete || engine.paywalled;
    if (isMobile && interactiveSurface && !engine.loading && actionRef.current) {
      actionRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }
    if (!engine.loading && canvasRef.current) {
      canvasRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, engine.loading, isMobile, engine.practice, engine.quiz, engine.retryState?.active, engine.lessonComplete, engine.paywalled]);

  // Read-only learner-state poll for the Why This Step gap pill. Mirrors how the
  // dashboard cards read consecutive_failures; never calls engine methods.
  useEffect(() => {
    if (!learner?.id || !trackId) return;
    getLearnerState(learner.id, trackId).then(setScLearnerState).catch(() => {});
  }, [learner?.id, trackId, engine.sessionPhase, engine.lessonData?.id]);

  // Adzuna job ticker — fetch on mount / track change. Fails silently.
  useEffect(() => {
    const appId  = import.meta.env.VITE_ADZUNA_APP_ID;
    const appKey = import.meta.env.VITE_ADZUNA_API_KEY;
    if (!appId || !appKey) return undefined;
    const what = TRACK_JOBS[trackId] || 'IT Support Technician';
    const url = `https://api.adzuna.com/v1/api/jobs/us/search/1?app_id=${appId}&app_key=${appKey}&what=${encodeURIComponent(what)}&category=it-jobs&results_per_page=15&content-type=application/json`;
    let cancelled = false;
    setJobs([]);
    fetch(url)
      .then(r => r.json())
      .then(data => { if (!cancelled && data && Array.isArray(data.results)) setJobs(data.results); })
      .catch(() => { /* hide ticker gracefully — no error shown */ });
    return () => { cancelled = true; };
  }, [trackId]);

  const handleNavigation = (text) => {
    const t = text.trim().toLowerCase();
    const modeMap = { '2': 'DRILL', 'drill': 'DRILL', '3': 'REVIEW', 'review': 'REVIEW', '4': 'COMPRESS', 'compress': 'COMPRESS', '1': null, 'continue': null };
    const requested = modeMap[t];
    if (requested !== undefined) engine.requestMode(requested || 'LESSON');
    handleSend(text);
  };

  // Stop the auto-advance countdown and reset the progress fill. Called whenever
  // the learner engages (sends a message, types, or acts on a button) so we never
  // advance the lesson out from under someone who's mid-thought.
  const cancelAutoAdvance = () => {
    clearTimeout(timerRef.current);
  };

  const handleSend = (text, opts = {}) => {
    cancelAutoAdvance();
    if (!opts.silent) {
      setChatHistory(prev => [...prev, { role: 'user', content: text }]);
    }
    engine.send(text, opts);
  };

  // Dispatch the single adaptive primary action to its handler.
  const runPrimaryAction = (action) => {
    if (action === 'RETAKE_QUIZ')         return engine.requestRecheck();
    if (action === 'CHECK_UNDERSTANDING') return handleSend('__CHECK_UNDERSTANDING__', { turnDirective: 'GO_TO_PRACTICE', silent: true });
    return handleSend('__CONTINUE_LESSON__', { silent: true }); // CONTINUE_LESSON
  };

  // Auto-advance: once a teaching turn settles on an auto-advanceable action, give
  // the learner time to read, then run it automatically. Durations are per-action
  // (CONTINUE_LESSON: 45s, CHECK_UNDERSTANDING: 30s). Runs SILENTLY — no visible
  // countdown cue. Any engagement (typing, a manual button, a sent message) cancels
  // it via the handlers above. Re-arms each turn because `engine.loading` toggles.
  const AUTO_ADVANCE_MS = { CONTINUE_LESSON: 45000, CHECK_UNDERSTANDING: 30000 };
  useEffect(() => {
    const DURATION = AUTO_ADVANCE_MS[engine.primaryAction];
    if (!DURATION || engine.loading) {
      clearTimeout(timerRef.current);
      return undefined;
    }
    const action = engine.primaryAction;
    timerRef.current = setTimeout(() => runPrimaryAction(action), DURATION);
    return () => clearTimeout(timerRef.current);
  }, [engine.primaryAction, engine.loading]);

  const inProfile   = engine.sessionPhase === 'PROFILE';
  const accentColor = PHASE_META[engine.sessionPhase]?.color || '#2dd4bf';
  const scFailures  = scLearnerState?.consecutive_failures ?? 0;
  // Full-screen checkpoint takes over the body when a quiz is active and no
  // higher-precedence surface (paywall / lesson-complete / retry) is showing.
  const showFullScreenQuiz = !!engine.quiz && !engine.paywalled && !engine.lessonComplete && !engine.retryState?.active;

  // Job ticker — one looping half (rendered twice for a seamless marquee).
  const tickerJobs = jobs.filter(j => j.title).slice(0, 12);
  const renderTickerHalf = (half) => (
    <span style={{ display: 'inline-flex', alignItems: 'center' }} aria-hidden={half === 'b'}>
      <span style={{ marginRight: 10 }}>💼</span>
      {tickerJobs.map((j, i) => {
        const s   = j.salary_max || j.salary_min;
        const sal = s ? `$${Math.round(s / 1000)}K` : null;
        const loc = j.location?.display_name;
        const label = [j.title, sal, loc].filter(Boolean).join(' — ');
        return (
          <span key={`${half}-${i}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <a href={j.redirect_url} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: 'none' }}>{label}</a>
            <span style={{ color: C.muted, margin: '0 14px' }}>·</span>
          </span>
        );
      })}
    </span>
  );

  return (
    <div style={{ height: isMobile ? 'auto' : '100%', minHeight: isMobile ? '100%' : undefined, overflow: isMobile ? 'visible' : 'hidden', background: C.bg, fontFamily: "'Inter','Segoe UI',sans-serif", display: 'flex', flexDirection: 'column', zoom: 1.0 }}>
      <style>{`
        .adaptos-topbar {
          padding-top: env(safe-area-inset-top, 8px) !important;
        }
        body { background: var(--bg-primary); }
        .lesson-btn {
          padding: 11px 14px;
          border-radius: 10px;
          border: 1px solid var(--border-medium);
          background: var(--bg-card);
          color: var(--text-primary);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .lesson-btn:hover {
          background: var(--bg-elevated);
          border-color: var(--border-medium);
        }
        .job-ticker-mask {
          overflow: hidden;
          -webkit-mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
          mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
        }
        .job-ticker-track {
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
          will-change: transform;
          animation: jobTicker 60s linear infinite;
        }
        .job-ticker-mask:hover .job-ticker-track { animation-play-state: paused; }
        .job-ticker-track a:hover { text-decoration: underline; }
        @keyframes jobTicker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes dot { 0%,80%,100% { opacity:.2; transform:scale(.7); } 40% { opacity:1; transform:scale(1); } }
      `}</style>

      {/* TOP BAR — standalone only. Embedded (inside the dashboard shell) uses a
          slim lesson header instead; the sidebar provides nav. */}
      {!embedded && (
      <div className="adaptos-topbar" style={{ minHeight: 56, padding: isMobile ? '8px 14px' : '10px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: isMobile ? 10 : 20, background: C.panel, position: 'sticky', top: 0, zIndex: 10, flexShrink: 0 }}>
        {/* LEFT — brand + breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0, flexShrink: 1 }}>
          <img src="/logo.jpg" alt="AdaptOS" style={{ height: 30, width: 'auto', objectFit: 'contain', flexShrink: 0 }}/>
          <div style={{ width: 1, height: 16, background: C.border, flexShrink: 0 }}/>
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)', flexShrink: 0, whiteSpace: 'nowrap' }}>{trackData.label}</span>
          {!isMobile && engine.lessonData && (
            <><div style={{ width: 1, height: 16, background: 'var(--border-medium)', flexShrink: 0 }}/><span style={{ fontSize: 14, fontWeight: 500, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>{engine.lessonData.title}</span></>
          )}
        </div>

        {/* CENTER — Adzuna job ticker (hidden gracefully when empty / mobile) */}
        {!isMobile && tickerJobs.length > 0 && (
          <div className="job-ticker-mask" style={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>
            <div className="job-ticker-track" style={{ fontSize: 12.5, fontWeight: 600 }}>
              {renderTickerHalf('a')}{renderTickerHalf('b')}
            </div>
          </div>
        )}

        {/* RIGHT — phase pips + controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0, marginRight: 6 }}>
              {['PROFILE','LESSON','CHECKPOINT','REMEDIATION','VALIDATION'].map(p => <PhasePip key={p} phase={p} currentPhase={engine.sessionPhase}/>)}
            </div>
          )}
          <button onClick={onSignOut} style={{ padding: '7px 13px', borderRadius: 8, background: C.subtle, border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>← Dashboard</button>
          {isMobile && (
            <button onClick={() => setShowPanel(p => !p)} style={{ padding: '7px 13px', borderRadius: 8, background: showPanel ? C.subtle : 'var(--brand-amber)', border: `1px solid ${showPanel ? C.border : 'var(--brand-amber)'}`, color: showPanel ? C.muted : '#ffffff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {showPanel ? 'Chat' : 'References'}
            </button>
          )}
        </div>
      </div>
      )}

      {/* Embedded lesson header — slim title + breadcrumb + save. The dashboard
          sidebar provides nav, so no logo / job ticker / phase pips / dashboard btn. */}
      {embedded && !showFullScreenQuiz && (
        <div style={{ padding: isMobile ? '14px 16px 0' : '20px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              {/* Lesson title — responsive clamp so long names (e.g.
                  "Motherboards, Buses, and Ports") don't wrap into 3–4 lines on
                  mobile. Scales between 1rem and 1.5rem with the viewport. */}
              <h1 style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)', fontWeight: 800, color: 'var(--text-primary)', margin: 0, lineHeight: 1.3 }}>
                {engine.lessonData?.title || 'Your Lesson'}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '5px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[engine.lessonData?.modules?.title, trackData.label].filter(Boolean).join(' • ')}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Mobile: toggle between the lesson chat and the References panel
                  (Adaptive Reference / Video / Notes). The standalone topbar is
                  hidden when embedded, so this is the only way to reach it. */}
              {isMobile && (
                <button onClick={() => setShowPanel(p => !p)} style={{ padding: '7px 13px', borderRadius: 8, background: showPanel ? C.subtle : 'var(--brand-amber)', border: `1px solid ${showPanel ? C.border : 'var(--brand-amber)'}`, color: showPanel ? C.muted : '#ffffff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {showPanel ? 'Chat' : 'References'}
                </button>
              )}
              {onExit && (
                <button className="lesson-btn" onClick={onExit} style={{ fontSize: 13, padding: '8px 14px' }}>
                  Save Progress
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen checkpoint — takes over the body when a quiz is active */}
      {showFullScreenQuiz && (
        <QuizPanel
          fullScreen
          quiz={engine.quiz}
          phase={engine.sessionPhase}
          onComplete={engine.handleQuizComplete}
          name={learner?.name}
        />
      )}

      {!showFullScreenQuiz && (<>
      {/* Five-step progress bar — reflects the engine's sessionPhase (desktop) */}
      {!isMobile && !inProfile && (
        <div style={{ borderBottom: `1px solid ${C.border}`, background: C.panel }}>
          <LessonProgressBar sessionPhase={engine.sessionPhase} />
        </div>
      )}

      {/* SPLIT BODY */}
      <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'visible' : 'hidden' }}>

        {/* LEFT — chat (hidden on mobile when panel is shown) */}
        {(!isMobile || !showPanel) && (
        <div style={{ flex: isMobile ? '0 0 auto' : '0 0 60%', display: 'flex', flexDirection: 'column', padding: isMobile ? '10px' : '14px 14px 14px 24px', overflow: isMobile ? 'visible' : 'hidden' }}>

          <MemoryPresence engine={engine} chatHistory={chatHistory} />

          {engine.modeOverride && (
            <div style={{ background: C.subtle, border: `1px solid ${C.border}`, borderRadius: 10, padding: '9px 14px', marginBottom: 8, flexShrink: 0 }}>
              <p style={{ fontSize: 11, color: accentColor, fontWeight: 600, margin: 0 }}>↻ {engine.modeOverride}</p>
            </div>
          )}

          {/* CHAT */}
          <div id="hint-chat-area" style={{ flex: isMobile ? '0 0 auto' : 1, minHeight: 0, overflowY: isMobile ? 'visible' : 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {(() => {
              // The current teaching turn = the latest assistant message → render
              // it as the focal canvas. Everything before it folds into the
              // collapsible transcript; any trailing user message (sent while a
              // reply loads) shows below the canvas.
              const canvasIdx = chatHistory.map(m => m.role).lastIndexOf('assistant');
              // If the focal answer was prompted by a learner question (the user
              // message immediately before it), keep that question visible above
              // the answer instead of folding it into the collapsed transcript —
              // so the question and its answer read together. Pulled out of
              // `earlier` to avoid showing it twice.
              const prevMsg   = canvasIdx > 0 ? chatHistory[canvasIdx - 1] : null;
              const promptIdx = prevMsg?.role === 'user' && isLearnerQuestion(prevMsg.content) ? canvasIdx - 1 : -1;
              const earlierEnd = promptIdx >= 0 ? promptIdx : canvasIdx;
              const earlier   = canvasIdx > 0 ? chatHistory.slice(0, earlierEnd) : [];
              const canvasPrompt = promptIdx >= 0 ? chatHistory[promptIdx] : null;
              const canvasMsg = canvasIdx >= 0 ? chatHistory[canvasIdx] : null;
              const trailing  = canvasIdx >= 0 ? chatHistory.slice(canvasIdx + 1) : chatHistory;
              return (
                <>
                  {earlier.length > 0 && <EarlierTranscript messages={earlier} />}
                  {canvasMsg && (
                    <div ref={canvasRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {canvasPrompt && <UserBubble content={canvasPrompt.content} />}
                      <ConceptCanvas content={canvasMsg.content} phase={canvasMsg.phase} diagram={canvasMsg.diagram} example={canvasMsg.example} masterySignal={canvasMsg.masterySignal} nextStep={canvasMsg.nextStep} />
                    </div>
                  )}
                  {trailing.map((msg, i) => (
                    msg.role === 'user'
                      ? <UserBubble key={`t${i}`} content={msg.content} />
                      : <AssistantMessage key={`t${i}`} content={msg.content} phase={msg.phase} />
                  ))}
                </>
              );
            })()}
            {engine.loading && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <MemoryOrb loading phase={engine.sessionPhase} small/>
                {/* Once tokens start streaming, render the live prose in place of
                    the typing dots (streaming flag on; empty until the first chunk). */}
                {engine.streamingText ? (
                  <AssistantMessage content={engine.streamingText} phase={engine.sessionPhase} />
                ) : (
                  <div style={{ background: C.cream, borderRadius: 12, borderTopLeftRadius: 4, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, animation: `dot 1.2s ease ${i*0.2}s infinite`, opacity: 0.4 }}/>)}
                      <style>{`@keyframes dot{0%,80%,100%{opacity:.2;transform:scale(.7)}40%{opacity:1;transform:scale(1)}}`}</style>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Transient note-save confirmation — cleared after 3s by the engine */}
            {engine.lastNote && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic', padding: '6px 12px', textAlign: 'center', opacity: 0.9 }}>
                ✓ Saved to Learning Notes — {engine.lastNote.split(/\s+/).slice(0, 6).join(' ')}…
              </div>
            )}
            <div ref={chatBottomRef}/>
          </div>

          <div ref={actionRef} style={{ paddingTop: 10, flexShrink: 0, scrollMarginBottom: isMobile ? 96 : 0 }}>
            {engine.paywalled ? (
              <LessonCompleteOverlay
                trialExhausted
                learner={learner}
                onExit={onSignOut}
              />
            ) : engine.lessonComplete ? (
              <LessonCompleteOverlay
                nextLesson={engine.nextLesson}
                onContinue={engine.advanceToNextLesson}
                onExit={onSignOut}
                recovery={engine.recheckPassed}
                completedLesson={engine.completedLesson}
                learner={learner}
              />
            ) : engine.retryState?.active ? (
              <CheckpointRetry state={engine.retryState} onRetry={engine.retryCheckpoint} onContinue={engine.continueAfterCheckpointFail}/>
            ) : engine.quiz ? (
              <QuizPanel
                quiz={engine.quiz}
                phase={engine.sessionPhase}
                onComplete={engine.handleQuizComplete}
              />
            ) : engine.practice ? (
              <PracticeCard
                practice={engine.practice}
                onComplete={engine.handlePracticeComplete}
                disabled={engine.loading}
              />
            ) : (
              // Teaching / remediation: ONE adaptive primary action (visually dominant),
              // an optional subordinate secondary, then the text input BELOW it. Surface
              // artifacts above (quiz / practice / overlay / retry) own the surface instead.
              <>
                {engine.primaryAction === 'REPAIRING' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px', borderRadius: 10, background: C.subtle, border: `1px solid ${C.border}` }}>
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      {[0,1,2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, animation: `dot 1.2s ease ${i*0.2}s infinite`, opacity: 0.4 }}/>)}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>AdaptOS is repairing the gap…</span>
                  </div>
                ) : engine.primaryAction && (
                  <>
                    {/* Primary action — visually dominant, full width. Auto-advances
                        silently after its timer; tapping advances now, typing/sending cancels. */}
                    <button
                      className="lesson-btn"
                      id="hint-continue-btn"
                      disabled={engine.loading}
                      onClick={() => {
                        // Manual Continue-Lesson click = first-run engagement (the
                        // silent 45s auto-advance bypasses this, calling runPrimaryAction
                        // directly — so only a real click counts).
                        if (engine.primaryAction === 'CONTINUE_LESSON') engine.engageLesson1('continue_click');
                        runPrimaryAction(engine.primaryAction);
                      }}
                      style={{ width: '100%', background: C.teal, borderColor: C.teal, color: C.tealText, fontSize: 15, padding: '13px 18px', opacity: engine.loading ? 0.55 : 1, cursor: engine.loading ? 'default' : 'pointer' }}
                    >
                      {PRIMARY_LABEL[engine.primaryAction]}
                    </button>
                    {/* Secondary — subordinate: smaller, muted, never equal weight */}
                    {engine.secondaryAction === 'KEEP_REPAIRING' && (
                      <button
                        className="lesson-btn"
                        onClick={() => handleSend('__CONTINUE_LESSON__', { silent: true })}
                        style={{ width: '100%', marginTop: 6, background: 'transparent', borderColor: C.border, color: C.muted, fontSize: 12, fontWeight: 600, padding: '8px 14px' }}
                      >
                        Keep Repairing
                      </button>
                    )}
                  </>
                )}
                {/* Text input — always present in teaching/remediation, BELOW the action */}
                <div style={{ marginTop: 10 }}>
                  <ChatInput
                    onSend={handleNavigation}
                    onInteract={cancelAutoAdvance}
                    loading={engine.loading}
                    phase={engine.sessionPhase}
                    placeholder={engine.loading ? 'Type a message…' : (PLACEHOLDER_BY_ACTION[engine.primaryAction] || 'Type a message…')}
                  />
                </div>
              </>
            )}
          </div>

          {!inProfile && (
            <div id="hint-chat-disclaimer">
              <WhyThisStep remediationActive={engine.remediationActive} repairDelivered={engine.repairDelivered} currentFocus={engine.currentFocus} failures={scFailures} />
            </div>
          )}
        </div>
        )}

        {/* DIVIDER — desktop only */}
        {!isMobile && <div style={{ width: 1, background: C.border, flexShrink: 0 }}/>}

        {/* RIGHT — full screen on mobile when shown */}
        {(!isMobile || showPanel) && (
        <div id="hint-study-tools-sidebar" style={{ flex: isMobile ? '0 0 auto' : '0 0 40%', padding: isMobile ? '10px' : '14px 24px 14px 14px', overflow: isMobile ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column', background: C.panel }}>
          <RightPanel isMobile={isMobile} reference={engine.reference} video={engine.video} autoVideoSignal={engine.autoVideoSignal} lessonData={engine.lessonData} trackData={trackData} sessionPhase={engine.sessionPhase} roadmapProgress={roadmapProgress} onSend={handleSend} breakVideo={engine.breakVideo} breakVideoLoading={engine.breakVideoLoading} breakVideoError={engine.breakVideoError} requestBreakVideo={engine.requestBreakVideo} clearBreakVideo={engine.clearBreakVideo} breakAllowed={!engine.quiz && !engine.practice} currentFocus={engine.currentFocus} learner={learner} lastNote={engine.lastNote}/>
        </div>
        )}
      </div>
      </>)}
    </div>
  );
}
